import type { MessageDescriptor } from 'react-intl';
import get from 'lodash/get';
import messages from './messages';
import type {
  FormUploadFieldElement,
  ListFieldElement,
  SelectFieldElement,
  UploadFieldElement,
} from '../../../../../types/form/Element';
import {
  TYPE_RADIO_FIELD,
  TYPE_UPLOAD_FIELD,
  TYPE_CHECKBOX_FIELD,
  TYPE_SELECT_FIELD,
} from '../../../../../constants';

import type { JoiError, TranslatedErrors } from './joi-type';

type Error = {
  bannerMessage?: MessageDescriptor;
  elementId?: string;
  message?: MessageDescriptor;
  panelMessage?: MessageDescriptor;
  logicId?: string;
};

type HandledErrorField =
  | FormUploadFieldElement
  | ListFieldElement
  | SelectFieldElement;

const LOGIC = 'logic';

export const extractElementId = (label: string): string | null => {
  const regexPattern = /element-[^.\s]+/;
  const match = label.match(regexPattern);
  return match ? match[0] : null;
};

// for use with translated errors
export const isDefaultError = (error: Error): boolean => {
  if (!error || !error.message) {
    return false;
  }
  return (
    error.message.defaultMessage === messages.defaultErrorInline.defaultMessage
  );
};

const isUploadField = (field: UploadFieldElement): boolean => {
  return field.type === TYPE_UPLOAD_FIELD;
};

const isOptionField = (
  field: SelectFieldElement | ListFieldElement,
): boolean => {
  if (field.type === TYPE_RADIO_FIELD || field.type === TYPE_CHECKBOX_FIELD) {
    return true;
  }
  const isOption =
    field.type === TYPE_SELECT_FIELD &&
    !Object.prototype.hasOwnProperty.call(field, 'metadataTemplateId');

  return isOption;
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const getLogicIdFromErrorPath = (path: Array<string>, content: any): string => {
  let start = 0;
  // skip the first path if it is content. We pass the content object directly to getTranslatedErrors, so we don't need to include it in the path
  if (path[0] === 'content') {
    start = 1;
  }
  // logic is an array. one level deeper into path is the logic index in the array
  // We add 2 here because Array.slice(start, end) is exclusive of the end index
  const end = path.indexOf(LOGIC) + 2;

  // this condition happens if 'logic' is not found in the path and indexOf returns -1;
  if (end === 1) {
    return '';
  }

  // We basically transform the JOI error path to the exact traversal path to the logic object's id
  // example
  // before: ['content', 'logic', 0, 'conditionGroups', 0, 'condition', 0]
  // after: ['logic', 0, 'id']
  const traversalPath = path.slice(start, end);
  traversalPath.push('id');

  const logicId = get(content, traversalPath, '');
  return logicId;
};

const NO_ITEMS =
  '"content.components.group-0.items" must contain at least 1 items';
const IS_NOT_ALLOWED_TO_BE_EMPTY = 'is not allowed to be empty';
const IS_REQUIRED = 'is required';
const MUST_BE_A_STRING = 'must be a string';
const MUST_CONTAIN_AT_LEAST_ONE_OPTION = 'must contain at least 1 items';
const CONTAINS_DUPLICATE_OPTIONS = 'contains a duplicate value';
const FIELD_ID_MUST_EXIST = 'fieldId must exist in content components';
const OPTION_VALUE_MUST_EXIST = 'value must exist in the list field options';

const isNoOptionsError = (message: string) => {
  return (
    message.endsWith(IS_REQUIRED) ||
    message.endsWith(MUST_CONTAIN_AT_LEAST_ONE_OPTION)
  );
};

const isDuplicateOptionsError = (message: string) => {
  return message.endsWith(CONTAINS_DUPLICATE_OPTIONS);
};

const isUploadFieldNoFolderError = (message: string) => {
  return (
    message.endsWith(MUST_BE_A_STRING) ||
    message.endsWith(IS_NOT_ALLOWED_TO_BE_EMPTY)
  );
};

const isFieldIdMustExistError = (message: string) => {
  return message.endsWith(FIELD_ID_MUST_EXIST);
};

const isOptionValueMustExistError = (message: string) => {
  return message.endsWith(OPTION_VALUE_MUST_EXIST);
};

const constructError = (
  bannerMessage: MessageDescriptor,
  elementId: string | null | undefined,
  message: MessageDescriptor,
  panelMessage: MessageDescriptor,
  logicId?: string | null,
): Error => {
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  const translatedError: Record<string, any> = {};
  if (bannerMessage) translatedError.bannerMessage = bannerMessage;
  if (elementId) translatedError.elementId = elementId;
  if (message) translatedError.message = message;
  if (panelMessage) translatedError.panelMessage = panelMessage;
  if (logicId) translatedError.logicId = logicId;
  return translatedError;
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const getError = (error: JoiError, content: any): Error => {
  const folderId = 'folderId';
  const options = 'options';

  let message: MessageDescriptor;
  let bannerMessage: MessageDescriptor;
  let panelMessage: MessageDescriptor;
  let field: HandledErrorField;
  let logicId;

  const { context, path, message: errorMessage } = error;
  const { label, key, value } = context;
  const elementId = extractElementId(label);

  if (elementId && content.components[elementId]) {
    // individual field level error
    field = content.components[elementId];
    if (
      isUploadField(field) &&
      key === folderId &&
      isUploadFieldNoFolderError(errorMessage)
    ) {
      // error when no folder is associated with upload field
      bannerMessage = messages.noFolderAssoicatedWithUploadBanner;
      panelMessage = messages.noFolderAssociatedWithUploadMessage;
      message = messages.noFolderAssociatedWithUploadMessage;
    } else if (
      isOptionField(field) &&
      key === options &&
      isNoOptionsError(errorMessage)
    ) {
      // error when no options are provided for dropdown / radio / checkbox field
      bannerMessage = messages.defaultErrorBanner;
      message = messages.noOptionsMessage;
    } else if (isOptionField(field) && isDuplicateOptionsError(errorMessage)) {
      // error when duplicate options are provided for dropdown / radio / checkbox field
      bannerMessage = messages.defaultErrorBanner;
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2339
      message = messages.duplicateOptionsMessage;
    } else {
      message = messages.defaultErrorInline;
    }
  } else if (errorMessage === NO_ITEMS) {
    // error when no fields are present
    bannerMessage = messages.noItemsBanner;
  } else if (
    // form level logic error
    label.includes(LOGIC) &&
    (isFieldIdMustExistError(errorMessage) ||
      isOptionValueMustExistError(errorMessage)) &&
    typeof value === 'object'
  ) {
    logicId = getLogicIdFromErrorPath(path, content);
  }

  return constructError(
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2454
    bannerMessage,
    elementId,
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2454
    message,
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2454
    panelMessage,
    logicId,
  );
};

export const getTranslatedErrors = (
  errors: JoiError[],
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  content: any,
): TranslatedErrors => {
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  const translatedErrors: Record<string, any> = {};
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7034
  let pageBannerMessage;
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  const logicErrors: Record<string, any> = {};

  const isOnlyLogicErrors = errors.every(error => error.path.includes(LOGIC));

  errors.forEach(error => {
    const translatedError = getError(error, content);
    const { bannerMessage, elementId, message, panelMessage, logicId } =
      translatedError;

    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7005
    if (pageBannerMessage) {
      pageBannerMessage = messages.defaultErrorBanner;
    } else {
      pageBannerMessage = bannerMessage;
    }

    if (elementId) {
      translatedErrors[elementId] = {};
      if (message) translatedErrors[elementId].message = message;
      if (panelMessage) translatedErrors[elementId].panelMessage = panelMessage;
    }
    if (logicId) {
      logicErrors[logicId] = true;
    }
  });

  // if there are only logic errors, we show generic logic error banner
  if (isOnlyLogicErrors) {
    pageBannerMessage = messages.formBuilderLogicErrorBanner;
  }

  if (!pageBannerMessage) {
    pageBannerMessage = messages.defaultErrorBanner;
  }
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
  return {
    bannerMessage: pageBannerMessage,
    logicErrors,
    ...translatedErrors,
  };
};
